import { SendPaymentResponse, Payment, LnurlPayResponse } from "@breeztech/breez-sdk-spark/web";
import { CurrencyType } from "../store/slices/walletSlice";
import { formatAmountWithFIAT, formatAmountWithFIATAndPlusMinus, formatAmountWithSATS, formatAmountWithSATSAndPlusMinus } from "../utils/Utils";
import { getTransactionDisplayAmount, getTransactionDisplayAmountFiat } from "../utils/transactionDisplay";
import { BulkTypes } from "./BulkPayment";
import { Pleb, PlebData } from "./Pleb";

export interface Transaction {
  id: string;
  status: TransactionStatus;
  user?: string;
  paymentHash: string;
  breezTxId?: string;
  amountMsat: number;
  amount: number;
  fee: number;
  opaFee?: number;
  routingFee?: number;
  comment: string;
  imageUrl?: string;
  time: number;
  read: boolean;
  announced?: boolean;
  btcTxId?: string;
  newBalance?: number;
  isInternal?: boolean;
  sender?: {
    type: PaymentMethod;
    lnurl?: string;
    invoice?: string;
    internalUser?: PlebData;
  };
  recipient?: {
    type: PaymentMethod;
    lnurl?: string;
    invoice?: string;
    internalUser?: PlebData;
  };
  type: PaymentType;
  bulk?: {
    type?: BulkTypes;
    name: string;
    imgUrl?: string;
    nbToSend: number;
    nbSendedSuccess: number;
    nbSendedError: number;
    geoZap?: {
      addressType?: string;
      countryCode?: string;
      countryImgUrl?: string;
    }
  },
  fromBulk?: {
    type?: BulkTypes;
    name?: string;
    imgUrl?: string;
    id?: string;
  },
  nostr?: {
    id: string;
    pubkey: string;
  },
  zapType: string;
  amountFiat?: number;
  opaFeeFiat?: number;
  routingFeeFiat?: number;
  feeFiat?: number;
  currencyCodeFiat?: string;
  additionalInfos?: AdditionalInfos,
  bulkTransactionId?: string;
  tapZapHash?: string;
  customTitle?: string;
  walletType: WalletType;
  clientSideTransaction?: boolean;
  invoice?: string;
  hidden?: boolean;
}

export interface AdditionalInfos {
  messageId?: string;
  channelId?: string;
  callId?: string;
  messageTapZap?: string;
  aggregatedTapZapAmount?: number;
  aggregatedTapZapAmountFiat?: number;
  isMigrationFromInternalToSpark?: boolean;
}

export enum WalletType {
  INTERNAL = 'internal',
  SPARK = 'spark',
}

export enum PaymentType {
  individual = 'individual',
  bulk = 'bulk',
  bulk_individual = 'bulk_individual',
}

export enum PaymentMethod {
  LNURL = 'lnurl',
  INVOICE = 'invoice',
  LNURL_WITHDRAW = 'lnurl_withdraw',
  SPARK = 'spark',
  ONCHAIN = 'onchain',
}

export enum TransactionStatus {
  PENDING = 'pending',
  SUCCESS = 'success',
}

export const apiResponseToTransaction = (apiResponse: any): Transaction => {
  const transaction = apiResponse as Transaction;
  if(transaction.recipient && transaction.recipient.internalUser)
    transaction.recipient.internalUser = Pleb.fromJson({user: apiResponse.recipient.internalUser}, '').toJson();
  if(transaction.sender && transaction.sender.internalUser)
    transaction.sender.internalUser = Pleb.fromJson({user: apiResponse.sender.internalUser}, '').toJson();
  if(transaction.amount < 2)
    transaction.announced = true;
  return transaction;
}

export const sparkResponseToTransaction = (sparkResponse: SendPaymentResponse | LnurlPayResponse | Payment): Transaction => {
  const payment = 'payment' in sparkResponse ? sparkResponse.payment : sparkResponse;
  let amount = Number(payment.amount);
  let read = false;
  if(payment.paymentType === 'send') {
    amount = -amount;
    read = true;
  }
  let comment = '';
  let invoice = undefined;
  if(payment.details?.type === 'lightning') {
    comment = payment.details?.description ?? payment.details?.lnurlReceiveMetadata?.senderComment ?? '';
    invoice = payment.details?.invoice ?? '';
  } else if(payment.details?.type === 'spark') {
    comment = payment.details.invoiceDetails?.description ?? '';
  }

  let btcTxId = undefined;

  if(payment.details?.type === 'withdraw') {
    btcTxId = payment.details.txId;
    read = true;
  } else if(payment.details?.type === 'deposit') {
    btcTxId = payment.details.txId;
    read = true;
  }
  
  return {
    id: payment.id,
    status: payment.status === 'completed' ? TransactionStatus.SUCCESS : TransactionStatus.PENDING,
    paymentHash: payment.id,
    amountMsat: amount * 1000,
    amount: amount,
    fee: Number(payment.fees),
    comment: comment,
    time: payment.timestamp * 1000,
    read: read,
    type: PaymentType.individual,
    zapType: 'Zap',
    walletType: WalletType.SPARK,
    invoice: invoice,
    clientSideTransaction: true,
    announced: true,
    btcTxId: btcTxId,
  }
}

export const renderAmount = (transaction: Transaction, currencyType: CurrencyType) => {
  const displayAmount = getTransactionDisplayAmount(transaction);
  const displayAmountFiat = getTransactionDisplayAmountFiat(transaction);

  if(currencyType === CurrencyType.FIAT && displayAmountFiat !== undefined && transaction.currencyCodeFiat !== undefined) {
    return formatAmountWithFIATAndPlusMinus(displayAmountFiat, transaction.currencyCodeFiat);
  }
  return formatAmountWithSATSAndPlusMinus(displayAmount);
}

export const renderFee = (transaction: Transaction, currencyType: CurrencyType) => {
  if(currencyType === CurrencyType.FIAT && transaction.feeFiat !== undefined && transaction.currencyCodeFiat !== undefined) {
    return formatAmountWithFIAT(Math.abs(transaction.feeFiat), transaction.currencyCodeFiat);
  }
  return formatAmountWithSATS(Math.abs(transaction.fee ?? 0));
}

export const renderOpaFee = (transaction: Transaction, currencyType: CurrencyType) => {
  if(currencyType === CurrencyType.FIAT && transaction.opaFeeFiat !== undefined && transaction.currencyCodeFiat !== undefined) {
    return formatAmountWithFIAT(Math.abs(transaction.opaFeeFiat), transaction.currencyCodeFiat);
  }
  return formatAmountWithSATS(Math.abs(transaction.opaFee ?? 0));
}

export const renderRoutingFee = (transaction: Transaction, currencyType: CurrencyType) => {
  if(currencyType === CurrencyType.FIAT && transaction.routingFeeFiat !== undefined && transaction.currencyCodeFiat !== undefined) {
    return formatAmountWithFIAT(Math.abs(transaction.routingFeeFiat), transaction.currencyCodeFiat);
  }
  return formatAmountWithSATS(Math.abs(transaction.routingFee ?? 0));
}
